import { isLocationDefined, isWindowDefined } from '@utils/validations';
import { DesignSizeTypeTypes } from 'graphql/generated/types.autogenerated';
import { GetTemplatesProps, GetTemplateLimit, FilterSelectionProp, GetTemplateFiltersType } from './constants';
import { urlParams } from '../../Modules/TemplatesModule/utils/urls';

export function setUrlQueryParams({ color, industry, size, style, type, page, query }: FilterSelectionProp) {
  if (!isWindowDefined || !isLocationDefined) {
    return;
  }

  const url =
    location.pathname +
    urlParams({
      query,
      color: color?.join(','),
      industry: industry?.join(','),
      size: size?.join(','),
      style: style?.join(','),
      type: type?.join(','),
      page: Number(page) <= 1 ? undefined : page,
    });

  history.replaceState({}, '', url);
}

export async function sanitizeSearchParams(searchParams: Promise<{ [key: string]: string | string[] }>) {
  const search = await searchParams;

  function fix(value: string | string[]) {
    return (typeof value === 'string' ? value.split(',').filter((x) => x) : value) ?? [];
  }

  return {
    color: fix(search.color),
    style: fix(search.style),
    type: fix(search.type),
    size: fix(search.size),
    industry: fix(search.industry),
    page: search.page && Number(search.page) < 1 ? '1' : ((search.page as string) ?? '1'),
    query: search.query as string,
  };
}

export function getFilters({ category, tag, size, filterSelection }: GetTemplateFiltersType) {
  const categoryIds = new Set([
    ...(category.documentCategories?.length ? category.documentCategories : []),
    ...((category.dbCategories
      ?.filter((x) => filterSelection.industry?.includes(x.label))
      ?.map((x) => Number(x.value)) as number[]) ?? []),
  ]);

  const designSizeTypeIds = new Set([
    ...(size.documentDesignSizeTypes?.length ? size.documentDesignSizeTypes : []),
    ...((size.dbDesignSizeTypes
      ?.filter((x) => filterSelection.size?.includes(x.label))
      ?.map((x) => Number(x.value)) as number[]) ?? []),
  ]);

  const tagIds = new Set([
    ...(tag.documentTags?.length ? tag.documentTags : []),
    ...((tag.dbStyles
      ?.filter((x) => filterSelection.style?.includes(x.label))
      ?.map((x) => Number(x.value)) as number[]) ?? []),
    ...((tag.dbColors
      ?.filter((x) => filterSelection.color?.includes(x.label))
      ?.map((x) => Number(x.value)) as number[]) ?? []),
  ]);

  const type = isType(DesignSizeTypeTypes.VIDEO, filterSelection)
    ? DesignSizeTypeTypes.VIDEO
    : isType(DesignSizeTypeTypes.ANIMATED, filterSelection)
      ? DesignSizeTypeTypes.ANIMATED
      : isType(DesignSizeTypeTypes.STATIC, filterSelection)
        ? DesignSizeTypeTypes.STATIC
        : undefined;

  return {
    categories: [...categoryIds],
    designSizeTypes: [...designSizeTypeIds],
    tags: [...tagIds],
    type,
    search: filterSelection.query,
  } as GetTemplatesProps;
}

export function isType(type: DesignSizeTypeTypes, filterSelection: FilterSelectionProp) {
  return filterSelection.type?.length === 1 && filterSelection.type[0]?.toString().toLowerCase() === type.toLowerCase();
}

export function checkIfArrayIsSame(arr1?: string[] | undefined, arr2?: (string | number)[] | null) {
  if (arr1?.length !== arr2?.length) {
    return false;
  }

  if (arr1?.length && arr2?.length) {
    return arr1.length === arr2.length && arr1.every((value) => arr2.some((x) => x === value));
  }
  return true;
}

export function getSelectedPageNumber(pageNumInUrl?: number | string | null, templatesCount?: number | null) {
  const num = Number(pageNumInUrl);
  if (Number.isNaN(num) || num < 1) {
    return 1;
  }
  if (templatesCount && num > Math.ceil((templatesCount ?? 0) / GetTemplateLimit)) {
    return Math.ceil((templatesCount ?? 0) / GetTemplateLimit);
  }
  return num;
}
